// Constants
import {
  ERROR_VERIFY,
  ONE_SECOND_MILIS,
  OTP_NEW_CODE_PROPS,
  OTP_RETRY_TIMEOUT_KEY,
} from '../constants/contract-otp.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
// Resolvers
import { requestNewCodeResolver, verifyCodeResolver } from '../resolvers/contract-otp.resolver';
// Translations
import translations from '../translations/contract-otp.translations';
// Types
import {
  CancelProcessHandlerType,
  ChangeOtpCodeHandlerType,
  ClearOtpHandlerType,
  ContractOtpHandlersReturnType,
  ContractOtpHandlersType,
  EnableNewOtpButtonHandlerType,
  GetOptInfoHandlerType,
  ResendOtpHandlerType,
  UpdateButtonConfigurationHandlerType,
  VerifyOtpHandlerType,
} from './types/contract-otp.handlers.type';
// Utils
import {
  getOtpDelay,
  getOtpInfo,
  getVerifyMessage,
  isAttemptsAvailable,
  isAttemptsNumberKnown,
  isDelayExpired,
  isNoMoreAttempts,
  isVerifyButtonDisabled,
  setOtpRequestTimeout,
} from './utils/contract-otp.handlers.utils';
import { isGilOtpError, isGilRiskError } from 'utils/resolver.utils';
import { navigateToCallbackUrl } from 'utils/app-callback-url.utils';

const cancelProcessHandler = ({
  handleConfirmDropOnboardingProcessButtonClick,
  otpInfo,
  verifyInfo,
}: CancelProcessHandlerType): void => {
  if (isNoMoreAttempts({ otpInfo, verifyInfo })) {
    handleConfirmDropOnboardingProcessButtonClick();
  }
};

export const clearOtpHandler = ({ setOtpInfo }: ClearOtpHandlerType): void => {
  setOtpInfo(info => ({ ...info, code: '' }));
};

export const changeOtpCodeHandler = ({ e, setOtpInfo }: ChangeOtpCodeHandlerType): void => {
  setOtpInfo(info => ({ ...info, code: e?.target?.value || '' }));
};

export const enableNewOtpButtonHandler = ({
  otpInfo,
  setOtpInfo,
}: EnableNewOtpButtonHandlerType): void => {
  if (isAttemptsNumberKnown(otpInfo)) {
    if (!isAttemptsAvailable(otpInfo)) {
      setOtpInfo(info => ({ ...info, delay: 0, disabled: true }));
    } else {
      const otpDelay = getOtpDelay();
      if (isDelayExpired(otpDelay)) {
        setOtpInfo(info => ({ ...info, delay: otpDelay, disabled: false }));
        localStorage.removeItem(OTP_RETRY_TIMEOUT_KEY);
      } else {
        setOtpInfo(info => ({
          ...info,
          disabled: true,
          delay: otpDelay,
        }));
        setTimeout(() => {
          enableNewOtpButtonHandler({ otpInfo, setOtpInfo });
        }, ONE_SECOND_MILIS);
      }
    }
  }
};

const getOtpInfoHandler = (props: GetOptInfoHandlerType): void => {
  getOtpInfo({
    formatMessage: props.formatMessage,
    setError: props.setError,
    setOtpInfo: props.setOtpInfo,
    setPhone: props.setPhone,
    setVerifyInfo: props.setVerifyInfo,
  });
};

const openCloseSnackbarHandler = ({ isOpen, setOtpInfo }) => {
  setOtpInfo(otp => ({ ...otp, open: isOpen }));
};

const resendOtpHandler = async (props: ResendOtpHandlerType): Promise<void> => {
  const {
    callbackUrl,
    formatMessage,
    handleClickResendOtpTracking,
    handleErrorMessageDisplayedTracking,
    loanId,
    otpInfo,
    setLastOtpRequestTimestamp,
    setOtpInfo,
    setVerifyInfo,
  } = props;
  if (!otpInfo.disabled) {
    handleClickResendOtpTracking();
    setOtpInfo(info => ({
      ...info,
      ...OTP_NEW_CODE_PROPS,
    }));
    if (otpInfo.attempts !== null && otpInfo.attempts > 1) {
      setOtpRequestTimeout({ setLastOtpRequestTimestamp });
    }
    const [errorCode, error] = await requestNewCodeResolver();
    await getOtpInfo(props);
    if (error) {
      if (errorCode && isGilOtpError(errorCode)) {
        navigateToCallbackUrl({ callbackUrl, loanId });
      } else {
        const errorMessage = formatMessage({ id: translations.OTP_RETRY_ERROR });
        setOtpInfo(info => ({
          ...info,
          disabled: false,
          error: true,
          msg: errorMessage,
          open: true,
        }));
        handleErrorMessageDisplayedTracking(errorMessage);
      }
    } else {
      setOtpInfo(info => ({
        ...info,
        error: false,
        msg: formatMessage({ id: translations.OTP_RETRY_SUCCESS }),
        open: true,
      }));
      setVerifyInfo(info => ({
        ...info,
        showAttemptsWarning: false,
      }));
    }
  }
};

const updateButtonConfigurationHandler = (props: UpdateButtonConfigurationHandlerType): void => {
  const { formatMessage, otpInfo, verifyInfo, setMainButtonConfiguration } = props;
  setMainButtonConfiguration({
    children: formatMessage({ id: translations.CONTINUE }),
    disabled: isVerifyButtonDisabled({ otpInfo, verifyInfo }),
    loading: verifyInfo.fetching,
    onClick: () => {
      props.handleContinueButtonClickTracking();
      verifyOtpHandler(props);
    },
  });
};

export const verifyOtpHandler = async (props: VerifyOtpHandlerType): Promise<void> => {
  const {
    callbackUrl,
    formatMessage,
    handleErrorMessageDisplayedTracking,
    loanId,
    otpInfo,
    setCurrentState,
    setError,
    setVerifyInfo,
  } = props;
  setVerifyInfo(info => ({
    ...info,
    fetching: true,
    msg: null,
    showAttemptsWarning: true,
  }));
  const [response, error] = await verifyCodeResolver({
    otpId: otpInfo.id,
    otpValue: otpInfo.code,
  });
  setVerifyInfo(info => ({
    ...info,
    fetching: false,
  }));
  if (error) {
    if (response === ERROR_VERIFY) {
      const { otpAttempts, verifyAttempts } = await getOtpInfo(props);
      const errorMessage = getVerifyMessage({
        formatMessage,
        otpAttempts,
        verifyAttempts,
      });
      setVerifyInfo(info => ({
        ...info,
        msg: errorMessage,
      }));
      handleErrorMessageDisplayedTracking(errorMessage);
    } else {
      isGilRiskError(response)
        ? navigateToCallbackUrl({ callbackUrl, loanId })
        : setError(AppErrorEnumeration.VERIFY_OTP_FAILED);
    }
  } else {
    setCurrentState(AppStatesEnumeration.LOADING);
  }
};

const ContractOtpHandlers = (props: ContractOtpHandlersType): ContractOtpHandlersReturnType => ({
  handleCancelProcess: () => cancelProcessHandler(props),
  handleClearOtp: () => clearOtpHandler(props),
  handleChangeOtpCode: e => changeOtpCodeHandler({ e, ...props }),
  handleEnableNewOtpButton: () => enableNewOtpButtonHandler(props),
  handleGetOtpInfo: () => {
    getOtpInfoHandler(props);
  },
  handleOpenCloseSnackbar: isOpen => {
    openCloseSnackbarHandler({ isOpen, setOtpInfo: props.setOtpInfo });
  },
  handleResendOtp: () => resendOtpHandler(props),
  handleUpdateButtonConfiguration: () => updateButtonConfigurationHandler(props),
  handleVerifyOtp: () => verifyOtpHandler(props),
});

export { ContractOtpHandlers };
